import { useCallback } from 'react';

import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { Picture } from '@/components/wpl/image/src/image';
import { wacToken } from '@/libraries/design/foundations';
import {
	isLocalizableHref,
	useLocalizedHref,
} from '@/libraries/web-framework/src/localization/use-localized-href';

// matches product ids in Contentful
export type ProductId =
	| 'atlas'
	| 'bamboo'
	| 'bitbucket'
	| 'compass'
	| 'confluence'
	| 'atlassian-access' // now guard
	| 'guard'
	| 'jira-software' // now just jira
	| 'jira'
	| 'jira-align'
	| 'jira-product-discovery'
	| 'jira-service-management'
	| 'loom'
	| 'opsgenie'
	| 'rovo'
	| 'statuspage'
	| 'trello';

export type UniformProduct = {
	id?: ProductId;
	name: string;
	description?: string;
	href?: string;
	className?: string;
	imgSrc?: string;
	imgAlt?: string;
	type?: 'primary' | 'secondary';
};

export const UniformProductCard = ({
	id,
	name,
	description,
	href,
	className,
	imgSrc,
	imgAlt,
	type = 'secondary',
}: UniformProduct) => {
	const localizedHref = useLocalizedHref(href ?? '');
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleCardClick = useCallback(
		(productKey: string) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'linkButton',
				actionSubjectId: 'productCard',
				source: 'customerStories',
				attributes: { product: productKey },
			}).fire();
		},
		[createAnalyticsEvent],
	);

	let normalizedId: string;
	if (id === 'jira-software') {
		normalizedId = 'jira';
	} else if (id === 'atlassian-access') {
		normalizedId = 'guard';
	} else if (id) {
		// The id for some products in Contentful have "-" in between (ex. jira-service-management), so we need to remove it to match data-product-key values in the global nav
		normalizedId = id.replace(/-/g, '');
	} else {
		normalizedId = name.replace(/ /g, '').toLowerCase();
	}

	const iconSize = type === 'primary' ? 44 : 32;

	const cardContent = (
		<>
			<div css={iconStyle}>
				<Picture
					image={{
						src:
							imgSrc ??
							'https://dam-cdn.atl.orangelogic.com/AssetLink/5sd7w3j2d7rdm7354yi413c5s1eo6s10.webp',
						alt: imgAlt ?? '',
						width: iconSize,
						height: iconSize,
					}}
				/>
			</div>
			<div css={[textContainerStyle, overrideTextStyling, !description && noDescriptionStyle]}>
				<div className="h6">{name}</div>
				{description && <div className="body-sm">{description}</div>}
			</div>
		</>
	);

	return (
		<div className={className} data-testid="wae-product-card" css={cardStyle}>
			{href ? (
				<a
					href={isLocalizableHref(href) ? localizedHref : href}
					target={isLocalizableHref(href) ? undefined : '_blank'}
					rel={isLocalizableHref(href) ? undefined : 'noreferrer'}
					onClick={() => handleCardClick(normalizedId)}
				>
					{cardContent}
				</a>
			) : (
				<>{cardContent}</>
			)}
		</div>
	);
};

const cardStyle = css({
	width: '100%',
	marginBlock: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a:hover .h6': {
		color: wacToken('color.primary.blue'),
	},
});

const iconStyle = css({
	display: 'flex',
	float: 'left',
	marginInlineEnd: token('space.150'),
	alignItems: 'center',
	justifyContent: 'center',
	height: '44px',
	width: '44px',
	alignSelf: 'baseline',
});

const textContainerStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.025'),
	paddingBlockStart: token('space.025'),
	minHeight: '44px',
});

const noDescriptionStyle = css({
	justifyContent: 'center',
});

const overrideTextStyling = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.h6': {
		lineHeight: '19.2px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.body-sm': {
		lineHeight: '20px',
	},
});

export default UniformProductCard;
